import * as React from 'react'
import { ReactElement } from 'react'
import { Card, makeStyles, Text, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import useEntity from '../../../../hooks/useEntity'
import Loading from '../../../../components/Loading'
import { usePageStyles } from '../../File/ViewFile'
import { CRMDetailProps, ViewCRMProps } from '../ViewCRM'

export type CRMFinanceItemDto = {
    fileId: string
    fileReference: string
    fileName: string
    amount: string
}

export type CRMFinanceSummaryDto = {
    openInvoices: {
        total: string
        currency: string
        items: CRMFinanceItemDto[]
    }
    unbilledWork: {
        total: string
        currency: string
        items: CRMFinanceItemDto[]
    }
}

const useStyles = makeStyles({
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalL,

        '@media screen and (max-width: 600px)': {
            gridTemplateColumns: '1fr',
        },
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: 'minmax(60px, 100px) auto auto',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: tokens.spacingVerticalS,
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: tokens.colorNeutralStroke1,
    },
})

export default function CRMFinanceTab(props: ViewCRMProps): ReactElement {
    const { t: translate } = useTranslation()
    const pageStyles = usePageStyles()
    const styles = useStyles()

    const [finance] = useEntity<CRMFinanceSummaryDto>(() => ({ url: `/party/${props.crmRelationId}/finance` }), props.crmRelationId || false)

    if (!finance.loaded) {
        return <Loading />
    }

    return (
        <Card>
            <div className={styles.grid}>
                {/* Open Invoices */}
                <div className={styles.section}>
                    <Text weight={'semibold'}>{translate('crm.finance.open')}</Text>
                    {finance.data?.openInvoices?.items?.map(item => (
                        <div key={item.fileId} className={styles.row}>
                            <Text size={200}>{item.fileReference}</Text>
                            <Text size={200}>{item.fileName}</Text>
                            <Text size={200}>{item.amount}</Text>
                        </div>
                    ))}
                    <div className={styles.totalRow}>
                        <Text weight={'semibold'}>{translate('crm.finance.total')}</Text>
                        <Text weight={'semibold'}>{finance.data?.openInvoices?.total}</Text>
                    </div>
                </div>

                {/* Unbilled Work */}
                <div className={styles.section}>
                    <Text weight={'semibold'}>{translate('crm.finance.unbilled')}</Text>
                    {finance.data?.unbilledWork?.items?.map(item => (
                        <div key={item.fileId} className={styles.row}>
                            <Text size={200}>{item.fileReference}</Text>
                            <Text size={200}>{item.fileName}</Text>
                            <Text size={200}>{item.amount}</Text>
                        </div>
                    ))}
                    <div className={styles.totalRow}>
                        <Text weight={'semibold'}>{translate('crm.finance.total')}</Text>
                        <Text weight={'semibold'}>{finance.data?.unbilledWork?.total}</Text>
                    </div>
                </div>
            </div>
        </Card>
    )
}
